import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { periodoVentas } from "../lib/periodo";
import api, { formatMoney, formatNumber, ABCD_ORDER, ABCD_COLORS, ABCD_LABELS } from "../lib/api";
import { FlaskConical, Loader2, ArrowLeft, Search, ArrowRight, TrendingUp, TrendingDown, Minus, Package } from "lucide-react";
import DrillDown from "../components/DrillDown";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";
import { formatMoney2 } from "../lib/api";
import { BannerFarmanaliza } from "../components/BannerFarmanaliza";
import { EntradasStockChart } from "../components/EntradasStockChart";
import { RankingStockAntiguo } from "../components/RankingStockAntiguo";
import { RankingMargenRotacion } from "../components/RankingMargenRotacion";

const SEG_META = {
  farmacia: { label: "Farmacia", sub: "CN ≥ 400.000", accent: "border-sky-500", badge: "bg-sky-100 text-sky-800" },
  parafarmacia: { label: "Parafarmacia", sub: "CN < 400.000", accent: "border-violet-500", badge: "bg-violet-100 text-violet-800" },
};

export default function LabAnalysis() {
  const [searchParams, setSearchParams] = useSearchParams();
  const segment = searchParams.get("segment") || "parafarmacia";
  const familiaFilter = searchParams.get("familia") || null;
  const [studyMeta, setStudyMeta] = useState(null);
  const [labs, setLabs] = useState({ farmacia: [], parafarmacia: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: studies } = await api.get("/studies");
        if (!studies?.length) { setLoading(false); return; }
        const latest = studies[0];
        setStudyMeta(latest);
        const params = {};
        if (familiaFilter) params.familia = familiaFilter;
        const { data } = await api.get(`/studies/${latest.id}/lab-analysis`, { params });
        setLabs(data.labs);
      } finally { setLoading(false); }
    })();
  }, [familiaFilter]);

  const setSegment = (s) => {
    const next = new URLSearchParams(searchParams);
    next.set("segment", s);
    next.delete("lab");
    setSearchParams(next, { replace: true });
  };
  const openLab = (lab) => navigate(`/labs/resumen?lab=${encodeURIComponent(lab)}&segment=${segment}`);

  // useSortable must be called before any early return (hooks rules)
  const list = labs[segment] || [];
  const filtered = search
    ? list.filter((l) => l.laboratorio.toLowerCase().includes(search.toLowerCase()))
    : list;
  const sort = useSortable(filtered, "sales", "desc");

  if (loading) return <div className="p-8 text-slate-500 flex gap-2"><Loader2 className="animate-spin" size={16} /> Cargando…</div>;
  if (!studyMeta) return <div className="p-8 text-slate-500">No tienes ningún estudio cargado. <Link to="/upload" className="text-blue-700 font-semibold">Sube uno primero</Link>.</div>;

  const meta = SEG_META[segment];

  return (
    <div className="p-8 fade-in" data-testid="lab-analysis-page">
      <BannerFarmanaliza compact image="/img/farmanaliza-proveedores.jpg?v=2" kicker="Proveedores" subtitle="Laboratorios y proveedores"
                         text="Ventas, stock y eficiencia de cada laboratorio para preparar tus visitas y pedidos." testid="banner-proveedores" />
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Análisis por canal</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
            <FlaskConical size={28} className="text-slate-400" /> Laboratorios
          </h1>
          {familiaFilter && (
            <div className="mt-2 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 text-sm" data-testid="family-filter-chip">
              <span className="text-blue-900">Familia: <b>{familiaFilter}</b></span>
              <button
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("familia");
                  setSearchParams(next, { replace: true });
                }}
                className="text-blue-700 hover:text-blue-900"
                data-testid="family-filter-clear"
                title="Quitar filtro"
              >×</button>
            </div>
          )}
        </div>
        <div className="text-xs text-slate-500">
          Estudio: <span className="font-semibold">{studyMeta.name}</span> · {studyMeta.period_days}d
        </div>
      </div>

      {/* Segment tabs */}
      <div className="flex gap-2 border-b border-slate-200 mb-6 mt-4">
        {["parafarmacia", "farmacia"].map((s) => (
          <button
            key={s}
            onClick={() => setSegment(s)}
            data-testid={`lab-segment-${s}`}
            className={
              "px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition " +
              (segment === s ? `border-slate-900 text-slate-900` : "border-transparent text-slate-500 hover:text-slate-700")
            }
          >
            {SEG_META[s].label}
            <span className="ml-2 text-[10px] font-mono text-slate-400">{labs[s]?.length || 0}</span>
          </button>
        ))}
      </div>

      <RankingStockAntiguo segment={segment} onOpenLab={openLab} />
      <RankingMargenRotacion segment={segment} />

      <div className={`mb-4 border-l-4 ${meta.accent} bg-white rounded-lg shadow-sm p-4 flex items-center justify-between flex-wrap gap-3`}>
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-mono font-bold px-2 py-1 rounded ${meta.badge}`}>{meta.label.toUpperCase()}</span>
            <span className="text-xs text-slate-500">{meta.sub}</span>
          </div>
          <p className="text-sm text-slate-700 mt-1.5">
            {sort.sortedRows.length.toLocaleString("es-ES")} laboratorios · Selecciona uno para ver el desglose por familias y comparar con la media del sector.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar laboratorio…"
              className="input pl-9 w-64"
              data-testid="lab-search-input"
            />
          </div>
          <ExportButtons
            filename={`laboratorios-${segment}${familiaFilter ? '-' + familiaFilter : ''}`}
            title={`Laboratorios · ${meta.label}${familiaFilter ? ' · ' + familiaFilter : ''}`}
            subtitle={`Estudio: ${studyMeta.name} · ${studyMeta.period_days} días`}
            sheetName={`${meta.label}`}
            columns={[
              { header: "#", key: (_r, i) => i + 1 },
              { header: "Laboratorio", key: "laboratorio" },
              { header: "Productos", key: "products", align: "right" },
              { header: "Stock (€)", key: "stock_value", format: (v) => (v || 0).toFixed(2), align: "right" },
              { header: "Ventas (€)", key: "sales", format: (v) => (v || 0).toFixed(2), align: "right" },
              { header: "Ventas anuales (€)", key: "sales_annual", format: (v) => (v || 0).toFixed(2), align: "right" },
              { header: "V/S", key: "ratio_vs", format: (v) => (v || 0).toFixed(2), align: "right" },
            ]}
            rows={sort.sortedRows}
          />
        </div>
      </div>

      {/* Labs table */}
      <div className="card overflow-x-auto">
        <table className="dense" data-testid="lab-list-table">
          <thead>
            <tr>
              <th>#</th>
              <th><SortHeader label="Laboratorio" k="laboratorio" sort={sort} defaultDir="asc" /></th>
              <th className="num"><SortHeader label="Productos" k="products" sort={sort} num /></th>
              <th className="num"><SortHeader label="Stock (€)" k="stock_value" sort={sort} num /></th>
              <th className="num"><SortHeader label="Ventas (€)" k="sales" sort={sort} num /></th>
              <th className="num"><SortHeader label="Ventas anuales est." k="sales_annual" sort={sort} num /></th>
              <th className="num"><SortHeader label="V/S" k="ratio_vs" sort={sort} num /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sort.sortedRows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-slate-500">Sin resultados.</td></tr>
            )}
            {sort.sortedRows.map((l, i) => (
              <tr
                key={l.laboratorio}
                onClick={() => openLab(l.laboratorio)}
                className="cursor-pointer hover:bg-slate-50"
                data-testid={`lab-row-${l.laboratorio}`}
              >
                <td className="font-mono text-slate-400">{i + 1}</td>
                <td className="font-semibold">{l.laboratorio}</td>
                <td className="num">{formatNumber(l.products)}</td>
                <td className="num">{formatMoney(l.stock_value)}</td>
                <td className="num">{formatMoney(l.sales)}</td>
                <td className="num text-slate-500">{formatMoney(l.sales_annual)}</td>
                <td className={"num font-bold " + ((l.ratio_vs || 0) >= 1 ? "text-emerald-700" : "text-red-600")}>
                  {(l.ratio_vs || 0).toFixed(2)}×
                </td>
                <td className="text-blue-700 text-xs font-semibold whitespace-nowrap">Ver resumen <ArrowRight size={11} className="inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
